// /apps/patient/src/pages/calendar/CalendarView.tsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isToday,
  parseISO,
  addDays,
  addWeeks,
  differenceInWeeks,
  differenceInDays,
  isBefore,
  isAfter
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  Clock,
  Activity,
  ChevronLeft,
  ChevronRight,
  Filter,
  CheckCircle,
  Users
} from 'lucide-react';
import { useFeatures } from '../../contexts/FeatureContext';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { WorkoutActionModal } from './WorkoutActionModal';
import { DaySwapModal } from '../../components/programs/DaySwapModal';

interface CalendarEvent {
  id: string;
  type: 'appointment' | 'exercise' | 'class';
  date: Date;
  time?: string;
  title: string;
  subtitle?: string;
  color: string;
  icon: any;
  // Exercise-specific fields
  prescription_id?: string;
  assignment_id?: string;
  module_count?: number;
  // New fields for workout actions (start/swap)
  assignment_type?: 'workout_plan' | 'rehab_program' | 'single_prescription';
  week_number?: number;
  day_of_week?: number;        // ISO day (1-7)
  is_swapped?: boolean;
  original_day?: number;       // If swapped, what was the original ISO day
  // Completion status
  is_completed?: boolean;
  session_id?: string;         // ID of the completed session (for navigation)
  // Class-specific fields
  class_session_id?: string;
  class_definition_id?: string;
  enrollment_id?: string;
}

// Cache for schedule data
interface ScheduleCache {
  [key: string]: {
    data: any;
    timestamp: number;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper functions for day conversion
// ISO: 1=Monday, 7=Sunday
// JS:  0=Sunday, 1=Monday, 6=Saturday
const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

// Schedule override interface
interface ScheduleOverride {
  week_number: number;
  original_day: number;
  new_day: number;
  created_at: string;
}

// Apply schedule overrides to a workout schedule item
const applyScheduleOverrides = (
  schedule: any[],
  weekNum: number,
  overrides: ScheduleOverride[] | undefined
): any[] => {
  if (!overrides || overrides.length === 0) return schedule;

  return schedule.map(item => {
    const override = overrides.find(
      o => o.week_number === weekNum && o.original_day === item.day_of_week
    );
    return override
      ? { ...item, day_of_week: override.new_day, _swapped: true, _original_day: item.day_of_week }
      : item;
  });
};

export function CalendarView() {
  const navigate = useNavigate();
  const { hasFeature } = useFeatures();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'appointments' | 'exercises'>('all');
  const [view, setView] = useState<'month' | 'week'>('month');
  const [scheduleCache] = useState<ScheduleCache>({});

  // Workout action modal state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showWorkoutActionModal, setShowWorkoutActionModal] = useState(false);

  // Day swap modal state
  const [showDaySwapModal, setShowDaySwapModal] = useState(false);

  // Helper function to get days for frequency (returns ISO days 1-7)
  const getDaysForFrequency = (frequencyPerWeek: number): number[] => {
    switch (frequencyPerWeek) {
      case 1: return [3]; // Wednesday
      case 2: return [2, 4]; // Tuesday, Thursday
      case 3: return [1, 3, 5]; // Monday, Wednesday, Friday
      case 4: return [1, 2, 4, 5]; // Mon, Tue, Thu, Fri
      case 5: return [1, 2, 3, 4, 5]; // Weekdays
      case 6: return [1, 2, 3, 4, 5, 6]; // Mon-Sat
      case 7: return [1, 2, 3, 4, 5, 6, 7]; // Daily (Mon-Sun)
      default: return [1, 3, 5]; // Default to MWF
    }
  };

  // Helper to get date for a specific ISO day of week starting from a given week start (Monday)
  const getDateForISODayInWeek = (weekStartMonday: Date, isoDayOfWeek: number): Date => {
    // weekStartMonday should be a Monday (ISO day 1)
    // isoDayOfWeek: 1=Mon, 2=Tue, ..., 7=Sun
    // Days to add from Monday: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
    const daysToAdd = isoDayOfWeek - 1;
    return addDays(weekStartMonday, daysToAdd);
  };

  // Helper to get the Monday of the week containing a given date
  const getMondayOfWeek = (date: Date): Date => {
    const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return addDays(date, -daysFromMonday);
  };

  // Fetch from cache or API
  const fetchWithCache = async (key: string, fetcher: () => Promise<any>) => {
    const cached = scheduleCache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    
    const data = await fetcher();
    scheduleCache[key] = {
      data,
      timestamp: Date.now()
    };
    return data;
  };

  // Fetch exercise schedule for a workout plan assignment
  const fetchWorkoutPlanSchedule = async (
    assignment: any,
    rangeStart: Date,
    rangeEnd: Date
  ): Promise<CalendarEvent[]> => {
    const events: CalendarEvent[] = [];

    try {
      // 1. Get workout plan metadata
      const planKey = `plan-${assignment.workout_plan_id}`;
      const workoutPlan = await fetchWithCache(planKey, () =>
        patientApiService.getWorkoutPlan(assignment.workout_plan_id)
      );

      // 2. Calculate program date boundaries
      const programStartDate = parseISO(assignment.start_date);
      const programStartMonday = getMondayOfWeek(programStartDate);
      const programDurationWeeks = workoutPlan.duration_weeks || 2;
      const programEndDate = assignment.end_date
        ? parseISO(assignment.end_date)
        : addDays(addWeeks(programStartMonday, programDurationWeeks), -1); // Last day of last week

      // Get schedule overrides from assignment
      const scheduleOverrides: ScheduleOverride[] = assignment.schedule_overrides || [];

      // 3. Iterate through each program week
      for (let weekNum = 1; weekNum <= programDurationWeeks; weekNum++) {
        // Calculate the actual calendar dates for this program week
        const weekStartMonday = addWeeks(programStartMonday, weekNum - 1);
        const weekEndSunday = addDays(weekStartMonday, 6);

        // Skip if this program week is entirely outside the view range
        if (isAfter(weekStartMonday, rangeEnd) || isBefore(weekEndSunday, rangeStart)) {
          continue;
        }

        // 4. Fetch this specific week's schedule
        const scheduleKey = `workout-${assignment.workout_plan_id}-week-${weekNum}`;
        let schedule = await fetchWithCache(scheduleKey, () =>
          patientApiService.getWorkoutWeekSchedule(assignment.workout_plan_id, weekNum)
        ).catch(() => []);

        // 5. Apply schedule overrides for this week
        schedule = applyScheduleOverrides(schedule, weekNum, scheduleOverrides);

        // 6. Map each scheduled workout to its actual calendar date
        schedule.forEach((item: any) => {
          // item.day_of_week is ISO format (1=Mon, 7=Sun) - may be modified by override
          const eventDate = getDateForISODayInWeek(weekStartMonday, item.day_of_week);

          // Only add if within view range AND within program duration
          if (eventDate >= rangeStart &&
              eventDate <= rangeEnd &&
              eventDate >= programStartDate &&
              eventDate <= programEndDate) {

            // Build subtitle with swap indicator
            let subtitle = `Week ${weekNum} • ${item.exercise_prescription?.modules?.length || 0} modules`;
            if (item._swapped) {
              subtitle += ' (swapped)';
            }

            events.push({
              id: `${assignment.id}-${item.id}-week${weekNum}`,
              type: 'exercise',
              date: eventDate,
              title: item.exercise_prescription?.name || 'Workout',
              subtitle,
              color: item._swapped ? 'bg-indigo-500' : 'bg-blue-500',
              icon: Activity,
              prescription_id: item.exercise_prescription_id,
              assignment_id: assignment.id,
              module_count: item.exercise_prescription?.modules?.length,
              // New fields for swap functionality
              assignment_type: 'workout_plan',
              week_number: weekNum,
              day_of_week: item.day_of_week,
              is_swapped: item._swapped || false,
              original_day: item._original_day
            });
          }
        });
      }
    } catch (error) {
      console.error(`Error fetching workout plan schedule for assignment ${assignment.id}:`, error);
    }

    return events;
  };

  // Fetch exercise schedule for a rehab program assignment
  const fetchRehabProgramSchedule = async (
    assignment: any,
    rangeStart: Date,
    rangeEnd: Date
  ): Promise<CalendarEvent[]> => {
    const events: CalendarEvent[] = [];

    try {
      // Get progress info to determine current phase
      const progressKey = `progress-${assignment.id}`;
      const progress = await fetchWithCache(progressKey, () =>
        patientApiService.getAssignmentProgress(assignment.id)
      ).catch(() => ({ current_phase: 1 }));

      const currentPhase = progress?.currentPhase || progress?.current_phase || 1;
      const prescriptionsKey = `rehab-${assignment.rehab_program_id}-phase-${currentPhase}`;

      const prescriptions = await fetchWithCache(prescriptionsKey, () =>
        patientApiService.getRehabPhaseSchedule(assignment.rehab_program_id, currentPhase)
      ).catch(() => []);

      // Calculate program boundaries
      const programStartDate = parseISO(assignment.start_date);
      const programEndDate = assignment.end_date ? parseISO(assignment.end_date) : null;

      // Generate events based on frequency
      prescriptions.forEach((item: any) => {
        const daysForFrequency = getDaysForFrequency(item.frequency_per_week || 3);

        // Iterate through each week in the view range
        let weekStartMonday = getMondayOfWeek(rangeStart);
        
        while (weekStartMonday <= rangeEnd) {
          daysForFrequency.forEach(isoDayOfWeek => {
            const eventDate = getDateForISODayInWeek(weekStartMonday, isoDayOfWeek);

            // Check all boundary conditions
            const withinViewRange = eventDate >= rangeStart && eventDate <= rangeEnd;
            const afterProgramStart = eventDate >= programStartDate;
            const beforeProgramEnd = !programEndDate || eventDate <= programEndDate;

            if (withinViewRange && afterProgramStart && beforeProgramEnd) {
              const exerciseCount = item.exercise_prescription?.exercises?.length ||
                item.exercise_prescription?.modules?.reduce((sum: number, m: any) =>
                  sum + (m.exercises?.length || 0), 0) || 0;

              events.push({
                id: `${assignment.id}-${item.id}-${eventDate.getTime()}`,
                type: 'exercise',
                date: eventDate,
                title: item.exercise_prescription?.name || 'Rehab Session',
                subtitle: `Phase ${currentPhase} • ${exerciseCount} exercises`,
                color: 'bg-green-500',
                icon: Activity,
                prescription_id: item.exercise_prescription_id,
                assignment_id: assignment.id,
                assignment_type: 'rehab_program'
              });
            }
          });
          weekStartMonday = addDays(weekStartMonday, 7);
        }
      });
    } catch (error) {
      console.error(`Error fetching rehab schedule for assignment ${assignment.id}:`, error);
    }

    return events;
  };

  // Fetch exercise schedule for a direct prescription assignment
  const fetchPrescriptionSchedule = async (
    assignment: any,
    rangeStart: Date,
    rangeEnd: Date
  ): Promise<CalendarEvent[]> => {
    const events: CalendarEvent[] = [];

    try {
      const prescriptionKey = `prescription-${assignment.exercise_prescription_id}`;
      const prescription = await fetchWithCache(prescriptionKey, () =>
        patientApiService.getExerciseProgram(assignment.exercise_prescription_id)
      );

      // Determine frequency from prescription
      let frequency = 3;
      if (prescription.schedule?.rehab_frequency?.times_per_week) {
        frequency = prescription.schedule.rehab_frequency.times_per_week;
      } else if (prescription.schedule?.recommended_frequency) {
        // Handle string format like "3x_week"
        const freqStr = prescription.schedule.recommended_frequency;
        const match = freqStr.match(/^(\d+)x_week$/);
        if (match) {
          frequency = parseInt(match[1], 10);
        }
      }

      const daysForFrequency = getDaysForFrequency(frequency);

      // Calculate boundaries
      const programStartDate = parseISO(assignment.start_date);
      const programEndDate = assignment.end_date ? parseISO(assignment.end_date) : null;

      // Generate events for each week in range
      let weekStartMonday = getMondayOfWeek(rangeStart);

      while (weekStartMonday <= rangeEnd) {
        daysForFrequency.forEach(isoDayOfWeek => {
          const eventDate = getDateForISODayInWeek(weekStartMonday, isoDayOfWeek);

          const withinViewRange = eventDate >= rangeStart && eventDate <= rangeEnd;
          const afterProgramStart = eventDate >= programStartDate;
          const beforeProgramEnd = !programEndDate || eventDate <= programEndDate;

          if (withinViewRange && afterProgramStart && beforeProgramEnd) {
            events.push({
              id: `${assignment.id}-${eventDate.getTime()}`,
              type: 'exercise',
              date: eventDate,
              title: prescription.name || 'Exercise Session',
              subtitle: prescription.description || `${frequency}x per week`,
              color: 'bg-teal-500',
              icon: Activity,
              prescription_id: assignment.exercise_prescription_id,
              assignment_id: assignment.id,
              assignment_type: 'single_prescription'
            });
          }
        });
        weekStartMonday = addDays(weekStartMonday, 7);
      }
    } catch (error) {
      console.error(`Error fetching prescription schedule for assignment ${assignment.id}:`, error);
    }

    return events;
  };

  // Main function to fetch exercise schedule based on assignment type
  const fetchExerciseSchedule = async (
    assignment: any,
    rangeStart: Date,
    rangeEnd: Date
  ): Promise<CalendarEvent[]> => {
    if (assignment.assignment_type === 'workout_plan' && assignment.workout_plan_id) {
      return fetchWorkoutPlanSchedule(assignment, rangeStart, rangeEnd);
    } else if (assignment.assignment_type === 'rehab_program' && assignment.rehab_program_id) {
      return fetchRehabProgramSchedule(assignment, rangeStart, rangeEnd);
    } else if (assignment.assignment_type === 'exercise_prescription' && assignment.exercise_prescription_id) {
      return fetchPrescriptionSchedule(assignment, rangeStart, rangeEnd);
    }
    return [];
  };

  // Main fetch function
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const allEvents: CalendarEvent[] = [];

    try {
      let rangeStart: Date;
      let rangeEnd: Date;

      if (view === 'week') {
        rangeStart = startOfWeek(currentDate);
        rangeEnd = endOfWeek(currentDate);
      } else {
        rangeStart = startOfMonth(currentDate);
        rangeEnd = endOfMonth(currentDate);
      }

      // Fetch appointments if enabled
      if (hasFeature('appointments')) {
        try {
          const appointments = await patientApiService.getMyAppointments();
          if (Array.isArray(appointments)) {
            appointments.forEach(apt => {
              const aptDate = parseISO(apt.appointment_date);
              if (aptDate >= rangeStart && aptDate <= rangeEnd && apt.status === 'scheduled') {
                allEvents.push({
                  id: apt.id,
                  type: 'appointment',
                  date: aptDate,
                  time: apt.start_time,
                  title: apt.appointment_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                  subtitle: apt.provider_name,
                  color: 'bg-purple-500',
                  icon: Clock
                });
              }
            });
          }
        } catch (error) {
          console.error('Error fetching appointments:', error);
        }
      }

      // Fetch exercise programs if enabled
      if (hasFeature('rehab_programs') || hasFeature('workout_plans')) {
        try {
          const assignments = await patientApiService.getMyExercisePrograms();

          if (Array.isArray(assignments)) {
            // Fetch schedules for all active assignments in parallel
            const exercisePromises = assignments
              .filter(a => a.status === 'active')
              .map(assignment => fetchExerciseSchedule(assignment, rangeStart, rangeEnd));

            const exerciseResults = await Promise.all(exercisePromises);
            exerciseResults.forEach(events => allEvents.push(...events));
          }
        } catch (error) {
          console.error('Error fetching exercise programs:', error);
        }
      }

      // Fetch class enrollments if enabled
      if (hasFeature('classes')) {
        try {
          const enrollments = await patientApiService.getMyClassEnrollments();
          if (Array.isArray(enrollments)) {
            enrollments
              .filter((e: any) => ['enrolled', 'checked_in', 'waitlisted'].includes(e.status))
              .forEach((enrollment: any) => {
                const session = enrollment.class_session;
                const definition = enrollment.class_definition;
                if (!session?.session_date) return;
                const sessionDate = parseISO(session.session_date);
                if (sessionDate < rangeStart || sessionDate > rangeEnd) return;
                allEvents.push({
                  id: `class-${enrollment.id}`,
                  type: 'class',
                  date: sessionDate,
                  time: session.start_time,
                  title: definition?.name || 'Class',
                  subtitle: definition?.description || undefined,
                  color: enrollment.status === 'waitlisted' ? 'bg-amber-500' : 'bg-indigo-500',
                  icon: Users,
                  class_session_id: session.id,
                  class_definition_id: definition?.id,
                  enrollment_id: enrollment.id,
                });
              });
          }
        } catch (error) {
          console.error('Error fetching class enrollments:', error);
        }
      }

      // Fetch session completions to mark completed events
      try {
        const completionsResponse = await patientApiService.getProgressSessions({
          start_date: format(rangeStart, 'yyyy-MM-dd'),
          end_date: format(rangeEnd, 'yyyy-MM-dd'),
          per_page: 100 // Get all completions for the range
        });

        const completions = completionsResponse?.sessions || [];

        // Create a map of completions by prescription_id + date for precise matching
        // This handles two-a-days where multiple workouts may be scheduled on the same day
        const completionMap = new Map<string, any>();
        completions.forEach((completion: any) => {
          // Primary key: prescription_id + date (most specific)
          if (completion.prescription_id) {
            const key = `${completion.prescription_id}-${completion.session_date}`;
            completionMap.set(key, completion);
          }
          // Fallback key: assignment_id + date (for older records without prescription_id)
          const fallbackKey = `fallback-${completion.assignment_id}-${completion.session_date}`;
          if (!completionMap.has(fallbackKey)) {
            completionMap.set(fallbackKey, completion);
          }
        });

        // Mark events as completed if there's a matching completion
        allEvents.forEach(event => {
          if (event.type === 'exercise' && event.assignment_id) {
            const dateKey = format(event.date, 'yyyy-MM-dd');

            // Try prescription_id match first (most specific)
            let completion = null;
            if (event.prescription_id) {
              const prescriptionKey = `${event.prescription_id}-${dateKey}`;
              completion = completionMap.get(prescriptionKey);
            }

            // Fallback to assignment_id match if no prescription match
            if (!completion) {
              const fallbackKey = `fallback-${event.assignment_id}-${dateKey}`;
              completion = completionMap.get(fallbackKey);
            }

            if (completion && (completion.status === 'completed' || completion.status === 'partial')) {
              event.is_completed = true;
              event.session_id = completion.id;
              // Update color to indicate completion
              event.color = completion.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500';
            }
          }
        });
      } catch (error) {
        console.error('Error fetching session completions:', error);
        // Continue without completion data - events will show as scheduled
      }

      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, view, hasFeature]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events based on selected filter
  const filteredEvents = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(event =>
      filter === 'appointments' ? event.type === 'appointment' : event.type === 'exercise'
    );
  }, [events, filter]);

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return filteredEvents.filter(event => isSameDay(event.date, day));
  };

  // Navigate months/weeks
  const navigatePeriod = (direction: 'prev' | 'next') => {
    if (view === 'week') {
      setCurrentDate(prev => addDays(prev, direction === 'next' ? 7 : -7));
    } else {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
        return newDate;
      });
    }
  };

  // Handle clicking on an exercise event
  const handleExerciseEventClick = (event: CalendarEvent) => {
    if (event.type === 'appointment') {
      navigate(`/appointments/${event.id}`);
      return;
    }

    if (event.type === 'class') {
      navigate('/my-classes');
      return;
    }

    // For completed sessions, navigate to progress detail
    if (event.is_completed && event.session_id) {
      navigate(`/progress/session/${event.session_id}`);
      return;
    }

    // For workout plans, show the action modal
    if (event.assignment_type === 'workout_plan') {
      setSelectedEvent(event);
      setShowWorkoutActionModal(true);
    } else {
      // For rehab programs and prescriptions, navigate directly to session
      if (event.prescription_id && event.assignment_id) {
        navigate(`/programs/${event.assignment_id}/session/${format(event.date, 'yyyy-MM-dd')}`);
      }
    }
  };

  // Handle starting a workout from the modal
  const handleStartWorkout = () => {
    if (selectedEvent?.prescription_id && selectedEvent?.assignment_id) {
      navigate(`/programs/${selectedEvent.assignment_id}/session/${format(selectedEvent.date, 'yyyy-MM-dd')}`);
    }
    setShowWorkoutActionModal(false);
    setSelectedEvent(null);
  };

  // Handle opening the day swap modal
  const handleOpenDaySwap = () => {
    setShowWorkoutActionModal(false);
    setShowDaySwapModal(true);
  };

  // Get scheduled workouts for the week (for DaySwapModal)
  const getScheduledWorkoutsForWeek = useCallback(() => {
    if (!selectedEvent?.assignment_id || !selectedEvent?.week_number) return [];

    // Filter events for this assignment and week
    return events
      .filter(e =>
        e.assignment_id === selectedEvent.assignment_id &&
        e.week_number === selectedEvent.week_number &&
        e.assignment_type === 'workout_plan'
      )
      .map(e => ({
        day: e.day_of_week!,
        prescriptionName: e.title,
        isSwapped: e.is_swapped
      }));
  }, [events, selectedEvent]);

  // Handle day swap
  const handleDaySwap = async (originalDay: number, newDay: number) => {
    if (!selectedEvent?.assignment_id || !selectedEvent?.week_number) return;

    // Get the current overrides to detect return-to-original scenarios
    const assignments = await patientApiService.getMyExercisePrograms();
    const assignment = assignments.find((a: any) => a.id === selectedEvent.assignment_id);
    const overrides = assignment?.schedule_overrides || [];

    // Find which workout is being displayed on the visual day (originalDay from modal's perspective)
    const overrideShowingOnVisualDay = overrides.find(
      (o: any) => o.week_number === selectedEvent.week_number && o.new_day === originalDay
    );
    const workoutOriginalDay = overrideShowingOnVisualDay
      ? overrideShowingOnVisualDay.original_day
      : originalDay;

    // Find which workout is on the target day
    const overrideShowingOnNewDay = overrides.find(
      (o: any) => o.week_number === selectedEvent.week_number && o.new_day === newDay
    );
    const targetWorkoutOriginalDay = overrideShowingOnNewDay
      ? overrideShowingOnNewDay.original_day
      : newDay;

    const isReturningHome = workoutOriginalDay === newDay;
    const targetIsReturningHome = targetWorkoutOriginalDay === originalDay;

    if (isReturningHome && targetIsReturningHome) {
      // Both returning to original - delete both overrides
      if (overrideShowingOnVisualDay) {
        await patientApiService.deleteScheduleOverride(selectedEvent.assignment_id, {
          week_number: selectedEvent.week_number,
          original_day: workoutOriginalDay
        });
      }
      if (overrideShowingOnNewDay) {
        await patientApiService.deleteScheduleOverride(selectedEvent.assignment_id, {
          week_number: selectedEvent.week_number,
          original_day: targetWorkoutOriginalDay
        });
      }
    } else if (isReturningHome) {
      // Single workout returning home
      if (overrideShowingOnVisualDay) {
        await patientApiService.deleteScheduleOverride(selectedEvent.assignment_id, {
          week_number: selectedEvent.week_number,
          original_day: workoutOriginalDay
        });
      }
      // Check for conflicting override
      const conflictingOverride = overrides.find(
        (o: any) => o.week_number === selectedEvent.week_number && o.new_day === newDay && o.original_day !== workoutOriginalDay
      );
      if (conflictingOverride) {
        await patientApiService.deleteScheduleOverride(selectedEvent.assignment_id, {
          week_number: selectedEvent.week_number,
          original_day: conflictingOverride.original_day
        });
      }
    } else {
      // Regular swap
      await patientApiService.createScheduleOverride(selectedEvent.assignment_id, {
        week_number: selectedEvent.week_number,
        original_day: workoutOriginalDay,
        new_day: newDay
      });
    }

    // Refresh events
    setShowDaySwapModal(false);
    setSelectedEvent(null);
    fetchEvents();
  };

  // Render calendar grid
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-gray-50 px-2 py-3 text-center text-sm font-medium text-gray-700">
            {day}
          </div>
        ))}

        {days.map(day => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();

          return (
            <div
              key={day.toISOString()}
              className={`
                min-h-[100px] bg-white p-2 cursor-pointer hover:bg-gray-50
                ${!isCurrentMonth ? 'text-gray-400' : ''}
                ${isToday(day) ? 'bg-blue-50' : ''}
                ${selectedDate && isSameDay(day, selectedDate) ? 'ring-2 ring-primary' : ''}
              `}
              onClick={() => setSelectedDate(day)}
            >
              <div className="font-medium text-sm mb-1">
                {format(day, 'd')}
              </div>

              <div className="space-y-1">
                {dayEvents.slice(0, 3).map(event => (
                  <div
                    key={event.id}
                    className={`
                      text-xs px-1 py-0.5 rounded truncate cursor-pointer flex items-center gap-1
                      ${event.color} text-white hover:opacity-80
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExerciseEventClick(event);
                    }}
                    title={event.title}
                  >
                    {event.is_completed && <CheckCircle className="h-3 w-3 flex-shrink-0" />}
                    <span className="truncate">{event.time ? `${event.time} - ` : ''}{event.title}</span>
                  </div>
                ))}

                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-500 px-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekEnd = endOfWeek(currentDate);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div className="space-y-4">
        {days.map(day => {
          const dayEvents = getEventsForDay(day);

          return (
            <div key={day.toISOString()} className={`
              bg-white rounded-lg p-4 border-2
              ${isToday(day) ? 'border-primary' : 'border-transparent'}
            `}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg">
                  {format(day, 'EEEE, MMMM d')}
                </h3>
                {isToday(day) && (
                  <span className="text-sm bg-primary text-white px-2 py-1 rounded">
                    Today
                  </span>
                )}
              </div>

              {dayEvents.length > 0 ? (
                <div className="space-y-2">
                  {dayEvents.map(event => (
                    <div
                      key={event.id}
                      className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer ${
                        event.is_completed
                          ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => handleExerciseEventClick(event)}
                    >
                      <div className={`p-2 rounded-lg ${event.color}`}>
                        {event.is_completed ? (
                          <CheckCircle className="h-5 w-5 text-white" />
                        ) : (
                          <event.icon className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{event.title}</span>
                          {event.is_completed && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Completed
                            </span>
                          )}
                        </div>
                        {event.subtitle && (
                          <div className="text-sm text-gray-600">{event.subtitle}</div>
                        )}
                        {event.time && (
                          <div className="text-sm text-gray-500 mt-1">{event.time}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No events scheduled</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Calendar"/>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Navigation */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigatePeriod('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <h2 className="text-xl font-semibold">
                {format(currentDate, view === 'week' ? "'Week of' MMM d, yyyy" : 'MMMM yyyy')}
              </h2>

              <button
                onClick={() => navigatePeriod('next')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Today
              </button>
            </div>

            {/* View and Filter Controls */}
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setView('month')}
                  className={`px-3 py-1 text-sm rounded ${
                    view === 'month' ? 'bg-white shadow-sm' : ''
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setView('week')}
                  className={`px-3 py-1 text-sm rounded ${
                    view === 'week' ? 'bg-white shadow-sm' : ''
                  }`}
                >
                  Week
                </button>
              </div>

              {/* Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Events</option>
                <option value="appointments">Appointments</option>
                <option value="exercises">Exercises</option>
              </select>
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        {loading ? (
          <div className="flex items-center justify-center h-96 bg-white rounded-lg shadow-sm">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {view === 'month' ? renderCalendar() : renderWeekView()}
          </div>
        )}

        {/* Selected Date Details */}
        {selectedDate && view === 'month' && (
          <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </h3>

            {getEventsForDay(selectedDate).length > 0 ? (
              <div className="space-y-3">
                {getEventsForDay(selectedDate).map(event => (
                  <div
                    key={event.id}
                    className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer ${
                      event.is_completed
                        ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => handleExerciseEventClick(event)}
                  >
                    <div className={`p-2 rounded-lg ${event.color}`}>
                      {event.is_completed ? (
                        <CheckCircle className="h-5 w-5 text-white" />
                      ) : (
                        <event.icon className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{event.title}</span>
                        {event.is_completed && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      {event.subtitle && (
                        <div className="text-sm text-gray-600">{event.subtitle}</div>
                      )}
                      {event.time && (
                        <div className="text-sm text-gray-500 mt-1">{event.time}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No events scheduled for this day</p>
            )}
          </div>
        )}
      </div>

      {/* Workout Action Modal */}
      {selectedEvent && (
        <WorkoutActionModal
          isOpen={showWorkoutActionModal}
          onClose={() => {
            setShowWorkoutActionModal(false);
            setSelectedEvent(null);
          }}
          onStartWorkout={handleStartWorkout}
          onSwapDay={handleOpenDaySwap}
          workoutName={selectedEvent.title}
          date={selectedEvent.date}
          subtitle={selectedEvent.subtitle}
          canSwap={selectedEvent.assignment_type === 'workout_plan'}
          isSwapped={selectedEvent.is_swapped}
        />
      )}

      {/* Day Swap Modal */}
      {selectedEvent && selectedEvent.day_of_week && (
        <DaySwapModal
          isOpen={showDaySwapModal}
          onClose={() => {
            setShowDaySwapModal(false);
            setSelectedEvent(null);
          }}
          onConfirm={handleDaySwap}
          currentDay={selectedEvent.day_of_week}
          currentWorkoutName={selectedEvent.title}
          weekNumber={selectedEvent.week_number || 1}
          scheduledWorkouts={getScheduledWorkoutsForWeek()}
        />
      )}
    </div>
  );
}